/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { defaultSanctioningBodyAtom, defaultSanctioningBodyFeedAtom, userPreferencesAtom } from 'jotai/userPreferences';
import { sanctioningBodyService } from 'services/sanctioningBody/SanctioningBodyService';
import { userPreferencesService } from 'services/userPreferences/UserPreferencesServices';
import { useCallback } from 'react';
import { OriginType, SanctioningBody, SanctioningBodyEnum, SanctioningBodySeries } from 'models/viewModels';
import { isIndyUser, isNascarUser } from 'components/auth/utils';

export const sanctioningBodiesAtom = atom<Promise<SanctioningBody[]>>(async (_get) => {
  return await sanctioningBodyService.getSanctioningBodies();
});

export const authorizedSanctioningBodiesSelector = atom<Promise<SanctioningBody[]>>(async (_get) => {
  const sanctioningBodies = await sanctioningBodyService.getSanctioningBodies();
  return (
    sanctioningBodies?.filter((sanctioningBody) => {
      return (
        // used in combined views. only supports NASCAR and INDYCAR
        (isNascarUser() && sanctioningBody.sanctioningBodyId === SanctioningBodyEnum.NASCAR.toString()) ||
        (isIndyUser() && sanctioningBody.sanctioningBodyId === SanctioningBodyEnum.INDYCAR.toString())
      );
    }) ?? []
  );
});

const sanctioningBodiesSeriesAtomFamily = atomFamily<string, Atom<Promise<SanctioningBodySeries[]>>>(
  (sanctioningBodyId: string) =>
    atom<Promise<SanctioningBodySeries[]>>(async (_get) => {
      const response = await sanctioningBodyService.getSeries(sanctioningBodyId);
      return response;
    })
);

export const seriesBySanctioningBodySelector = atom<Promise<Map<SanctioningBody, SanctioningBodySeries[]>>>(
  async (get) => {
    const sanctioningBodies = await get(sanctioningBodiesAtom);
    const map = new Map<SanctioningBody, SanctioningBodySeries[]>();
    await Promise.all(
      sanctioningBodies.map(async (sanctioningBody) => {
        const sanctioningBodiesSeries = await get(sanctioningBodiesSeriesAtomFamily(sanctioningBody.sanctioningBodyId));
        map.set(sanctioningBody, sanctioningBodiesSeries ? sanctioningBodiesSeries : []);
      })
    );
    return map;
  }
);

export const activeSanctioningBodyIdAtom = atom<SanctioningBodyEnum | undefined>(undefined);

export const activeSanctioningBodyFeedAtom = atom<OriginType | undefined>(undefined);

export const activeSanctioningBodySeriesSelector = atom<Promise<SanctioningBodySeries[]>>(async (get) => {
  const activeSanctioningBodyId = get(activeSanctioningBodyIdAtom);
  let series: SanctioningBodySeries[] = [];
  if (activeSanctioningBodyId) {
    const actualSeries = await get(sanctioningBodiesSeriesAtomFamily(activeSanctioningBodyId.toString()));
    series = actualSeries ? actualSeries : [];
  }
  return series;
});

export const activeSanctioningBodySeriesIdsSelector = atom<Promise<string[]>>(async (get) => {
  const seriesList = await get(activeSanctioningBodySeriesSelector);
  return seriesList.map((series) => series.seriesId);
});

export const activeSanctioningBodySeriesBySanctioningBodyIdSelector = atomFamily<string, Atom<Promise<string[]>>>(
  (id: string) =>
    atom(async (get) => {
      const sanctioningBodiesSeries = await get(sanctioningBodiesSeriesAtomFamily(id));
      return sanctioningBodiesSeries ? sanctioningBodiesSeries.map((series) => series.seriesId) : [];
    })
);

export const sanctioningBodyBySeriesSelector = atomFamily<string, Atom<Promise<SanctioningBody>>>((seriesId: string) =>
  atom<Promise<SanctioningBody>>(async (get) => {
    const allSeriesBySanctioningBody = await get(seriesBySanctioningBodySelector);
    const sanctioningBody = Array.from(allSeriesBySanctioningBody.keys()).find((sanctioningBody) =>
      allSeriesBySanctioningBody.get(sanctioningBody)?.find((series) => series.seriesId === seriesId)
    );
    return sanctioningBody ? sanctioningBody : ({} as SanctioningBody);
  })
);

export const useUpsertSanctioningBodyDefaults = () => {
  return useAtomCallback(
    useCallback(async (get, set, sanctioningBodyId?: string, feedSource?: OriginType) => {
      const userPreferences = get(userPreferencesAtom);
      if (Object.keys(userPreferences).length > 0) {
        userPreferences.defaultSanctioningBody = sanctioningBodyId;
        userPreferences.defaultSanctioningBodyFeed = feedSource;
        set(userPreferencesAtom, { ...userPreferences });
      }
      if (await userPreferencesService.setDefaultSanctioningBody(sanctioningBodyId, feedSource)) {
        set(defaultSanctioningBodyAtom, sanctioningBodyId);
        set(defaultSanctioningBodyFeedAtom, feedSource);
      }
      return true;
    }, [])
  );
};

export const isIndyCarSanctioningBodySelector = atom<boolean>((get) => {
  const sanctioningBody = get(activeSanctioningBodyIdAtom);
  return sanctioningBody === SanctioningBodyEnum.INDYCAR;
});
